import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
	Alert,
	Card,
	CardBody,
	CardHeader,
	Col,
	Container,
	Input,
	Label,
	Nav,
	NavItem,
	NavLink,
	Row,
	Spinner,
	TabContent,
	TabPane,
	Table,
} from 'reactstrap';
import { createSelector } from 'reselect';
import {
	getActivityList,
	getActivitiesQuery,
	getActivityStatusList,
	getProductiveCycleActive,
} from '../../../../../slices/thunks';
import { ODataGrid } from '../../../../../Components/OElements/ODataGrid';
import { Link, useParams } from 'react-router-dom';
import AddEditEvidenceModal from '../../Add_Edit/AddEditEvidenceModal';
import UploadEvidenceModal from '../../Modals/Evidence/UploadEvidenceModal';
import GanttChart from '../Widgets/Gantt/GanttActivities';
import OffcanvasContainerActivity from '../../OffcanvasContainers/OffcanvasContainerActivity';
import SectionActivity from '../../Sections/SectionActivity';
import classnames from 'classnames';

const ProductiveCycleActivitySection = query => {
	// Dispatch & Params
	const dispatch = useDispatch();
	const { id } = useParams();

	// States
	const [isRight, setIsRight] = useState(false);
	const [filters, setFilters] = useState({
		dateRange: [],
		establishment_id: id,
		productive_cycle_id: null,
		activity_status_id: null,
	});
	const [listTasks, setListTasks] = useState([]);
	const [dataActivity, setDataActivity] = useState({});
	const [isEdit, setIsEdit] = useState(false);
	const [modal, setModal] = useState(false);
	const [modalUploadEvidence, setModalUploadEvidence] = useState(false);
	const [activeTab, setActiveTab] = useState('PND');

	// Selectors
	const selector = createSelector(
		state => state.Activities,
		activities => ({
			data: activities.itemActivityList,
			activityStatus: activities.itemActivityStatusList,
			isLoading: activities.isActivityLoading,
			isSuccess: activities.isSuccess,
			isError: activities.isError,
		})
	);
	const { data, activityStatus, isLoading, isSuccess, isError } =
		useSelector(selector);

	const selectorStage = createSelector(
		state => state.Establishments,
		stage => ({
			productive_cycle_stage: stage.productiveCycleActiveDetail,
		})
	);
	const { productive_cycle_stage } = useSelector(selectorStage);

	// Handlers
	const toggleRightCanvas = useCallback(() => {
		setIsRight(prevIsRight => !prevIsRight);
	}, []);

	const handleFiltersSubmit = useCallback(newFilters => {
		setFilters(newFilters);
	}, []);

	const handleToggleModal = () => {
		setModal(!modal);
	};

	const handleToggleModalUploadEvidence = () => {
		setModalUploadEvidence(!modalUploadEvidence);
	};

	const handleClickEdit = activity => {
		setDataActivity(activity);
		handleToggleModal();
	};

	const handleClickUploadEvidence = activity => {
		setDataActivity(activity);
		handleToggleModalUploadEvidence();
	};

	const toggleTab = tab => {
		if (activeTab !== tab) {
			setActiveTab(tab);
			const selectedStatus = activityStatus.find(
				status => status.code === tab
			);
			setFilters(prevFilters => ({
				...prevFilters,
				activity_status_id: selectedStatus ? selectedStatus._id : null,
			}));
		}
	};

	// Methods
	const createTasksFromData = data => {
		if (!Array.isArray(data)) return []; // Retorna un array vacío si data no es válida

		return data
			.filter(item => {
				const isValid =
					item &&
					item.start &&
					item.end &&
					item.title &&
					item.activity_group?.name;
				if (!isValid) {
					console.log('Elemento excluido:', item);
				}
				return isValid;
			})
			.map(item => {
				return {
					start: new Date(item.start),
					end: new Date(item.end),
					name: item.title,
					id: item._id,
					type: 'milestone',
					progress:
						item.activity_status?.code == 'CMP' ||
						item.activity_status?.code == 'APR'
							? 100
							: 0,
					project: item.productive_cycle_stage._id,
				};
			});
	};

	const createTasksFromStage = data => {
		if (!Array.isArray(data.stages)) return []; // Verifica que "stages" es un array válido

		return data.stages
			.filter(item => {
				const isValid = item && item.date_ini && item.date_end && item.name;
				if (!isValid) {
					console.log('Elemento excluido:', item);
				}
				return isValid;
			})
			.map((item, index) => {
				// Genera un ID único si no existe "_id"
				return {
					start: new Date(item.date_ini),
					end: new Date(item.date_end),
					name: item.name,
					id: item.productive_cycle_stage_id || `stage-${index}`, // Usa "productive_cycle_stage_id" o genera uno
					type: 'project',
					progress: 0,
					hideChildren: true,
				};
			});
	};

	// Effects
	useEffect(() => {
		dispatch(getActivityStatusList());
	}, []);

	useEffect(() => {
		if (activityStatus) {
			dispatch(getProductiveCycleActive(id)).then(stages => {
				if (stages && stages.payload) {
					setFilters(prevFilters => ({
						...prevFilters,
						productive_cycle_id: stages.payload._id,
					}));
				}
			});
		}
	}, [activityStatus]);

	useEffect(() => {
		if (id && productive_cycle_stage && activeTab === 'GANTT') {
			dispatch(getActivityList({ ...filters }));
		}
	}, [filters, id]);

	useEffect(() => {
		let tasksFromData = [];
		let tasksFromStage = [];
		if (data && Array.isArray(data)) {
			tasksFromData = createTasksFromData(data);
		}
		if (productive_cycle_stage) {
			tasksFromStage = createTasksFromStage(productive_cycle_stage);
		}

		// Ordena las tareas y ajusta el progreso de las etapas
		const orderedTasks = tasksFromStage.map(stageTask => {
			// Filtra las tareas que dependen de esta etapa
			const matchingTasks = tasksFromData.filter(
				dataTask => dataTask.project === stageTask.id
			);

			// Calcula el progreso promedio de las tareas dependientes
			const totalProgress = matchingTasks.reduce(
				(sum, task) => sum + task.progress,
				0
			);
			const averageProgress =
				matchingTasks.length > 0 ? totalProgress / matchingTasks.length : 0;

			// Actualiza el progreso de la etapa
			return {
				...stageTask,
				progress: averageProgress,
			};
		});

		// Inserta las tareas dependientes después de cada etapa correspondiente
		const finalOrderedTasks = orderedTasks.reduce((result, stageTask) => {
			result.push(stageTask);
			const matchingTasks = tasksFromData.filter(
				dataTask => dataTask.project === stageTask.id
			);
			result.push(...matchingTasks);
			return result;
		}, []);

		setListTasks(finalOrderedTasks);
	}, [data, productive_cycle_stage]);

	// Columns
	const columns = useMemo(
		() => [
			{
				header: 'Acciones', enableSorting: false, enableColumnFilter: false, enableGlobalFilter: false, enableColumnActions: false,
				columnDefType: 'display',
				
				width: 10,
				Cell: cellProps => {
					const activity = cellProps.row.original;
					return (
						<React.Fragment>
							<div className='d-flex'>
								<div className='flex-shrink-0'>
									<ul className='list-inline mb-0'>
										<li className='list-inline-item'>
											<Link
												title={'Ver Actividad'}
												to={`/establishment/activity/${cellProps.row.original._id}`}
											>
												<i className={`${Icons.policy} align-bottom me-2 text-muted`}></i>
											</Link>
										</li>
										<li className='list-inline-item'>
											<Link
												to='#'
												onClick={() => {
													if (!activity?.document_ids?.length) {
														handleClickUploadEvidence(activity);
													}
												}}
												title={`Cargar Evidencia${
													!activity?.document_ids?.length
														? ''
														: ' (No disponible)'
												}`}
											>
												<i
													className={`ri-upload-2-line align-bottom me-2 text-muted ${
														!activity?.document_ids?.length
															? ''
															: 'opacity-50'
													}`}
												></i>
											</Link>
										</li>
									</ul>
								</div>
							</div>
						</React.Fragment>
					);
				},
			},
			{
				header: 'Estado',
				accessorKey: 'activity_status.name',
				
				width: 10,
				Cell: ({ row }) => {
					const { bgcolor, txcolor, icon, name } =
						row.original.activity_status || {};
					return (
						<div>
							{name && (
								<span
									className='badge'
									style={{ backgroundColor: bgcolor, color: txcolor }}
								>
									<i className={`align-middle ${icon}`}></i> {name}
								</span>
							)}
						</div>
					);
				},
			},
			{
				header: 'Tipo',
				accessorKey: 'activity_type.name',
				
				width: 10,
			},
			{
				header: 'Nombre',
				accessorKey: 'title',
				
				width: '30%',
				Cell: ({ row }) => {
					return <div className='no-nowrap'>{row.original.title}</div>;
				},
			},
			{
				header: 'Prioridad',
				accessorKey: 'activity_priority.name',
				
				width: 10,
				Cell: ({ row }) => {
					const { bgcolor, txcolor, icon, name } =
						row.original.activity_priority || {};
					return (
						<div>
							{name && (
								<span
									className='badge'
									style={{ backgroundColor: bgcolor, color: txcolor }}
								>
									<i className={`align-middle ${icon}`}></i> {name}
								</span>
							)}
						</div>
					);
				},
			},
			{
				header: 'Cilo Productivo',
				accessorKey: 'productive_cycle.code',
				
				width: 10,
			},
			{
				header: 'Fecha',
				accessorKey: 'start',
				
				dataType: 'date',
				width: 10,
			},
			{
				header: 'Etapa',
				accessorKey: 'productive_cycle_stage.name',
				
				width: 10,
			},
			{
				header: 'Área Responsable',
				accessorFn: row =>
					Array.isArray(row.management) ? row.management.map(item => item.name).join(', ') : '',
				
				width: 10,
			},
			
			{
				header: 'Entidad Supervisora',
				accessorFn: row =>
					Array.isArray(row.supervisory_entity) ? row.supervisory_entity.map(item => item.name).join(', ') : '',
				
				width: 10,
			},
			
			{
				header: 'Grupo',
				accessorKey: 'activity_group.name',
				
				width: 10,
				Cell: ({ row }) => {
					const { bgcolor, txcolor, icon, name } =
						row.original.activity_group || {};
					return (
						<div>
							{name && (
								<span
									className='badge'
									style={{ backgroundColor: bgcolor, color: txcolor }}
								>
									<i className={`align-middle ${icon}`}></i> {name}
								</span>
							)}
						</div>
					);
				},
			},
		],
		[handleClickEdit]
	);

	const [selectionList, setSelectionList] = useState([]);
	const [refetchData, setRefetchData] = useState();

	return (
		<React.Fragment>
			<Card>
				<CardHeader>
					<div>
						<SectionActivity rightCanvasBtn={toggleRightCanvas} />
						<OffcanvasContainerActivity
							isOpen={isRight}
							toggle={toggleRightCanvas}
							filters={filters}
							onSubmit={handleFiltersSubmit}
						/>
					</div>
					<div>
						<Nav
							className='nav-tabs-custom rounded card-header-tabs border-bottom-0'
							role='tablist'
						>
							{activityStatus.map(status => (
								<NavItem key={status._id}>
									<NavLink
										style={{ cursor: 'pointer' }}
										className={classnames({
											active: activeTab === status.code,
										})}
										onClick={() => toggleTab(status.code)}
									>
										{status.name}
									</NavLink>
								</NavItem>
							))}
							<NavItem key='gantt'>
								<NavLink
									style={{ cursor: 'pointer' }}
									className={classnames({
										active: activeTab === 'GANTT',
									})}
									onClick={() => toggleTab('GANTT')}
								>
									Gantt
								</NavLink>
							</NavItem>
						</Nav>
					</div>
				</CardHeader>
				<CardBody>
					<TabContent activeTab={activeTab} className='text-muted p-0'>
						{activityStatus.map(status => (
							<TabPane
								key={status._id}
								tabId={status.code}
								id={status.code.toLowerCase()}
							></TabPane>
						))}
					</TabContent>
					<div>
						{isLoading ? (
							<Alert color='primary'>
								<Spinner size='sm' className='me-2' />
								Cargando datos, por favor espere...
							</Alert>
						) : activeTab === 'GANTT' ? (
							<TabPane key='gantt' tabId='GANTT' id='gantt'>
								{!isLoading && listTasks && listTasks.length > 0 ? (
									<GanttChart listTasks={listTasks} />
								) : null}
							</TabPane>
						) : (
							<ODataGrid
								key={`${activeTab}`}
								getDataFunc={getActivitiesQuery}
								columns={columns}
								remoteOperations={{
									pagination: true,
									sorting: true,
									filtering: true,
								}}
								queryPopulate={[
									'activity_status',
									'activity_group',
									'management',
									'supervisory_entity',
									'activity_type',
									'activity_priority',
									'user_info',
									'productive_cycle',
									'productive_cycle_stage',
									'establishment',
								]}
								baseFilters={{
									establishment_id: {
										$eq: id
									},
									disable: {
										$eq: false,
									},
									...(activeTab &&
										activeTab != 'GANTT' && {
											activity_status: {
												code: {
													$eq: activeTab,
												},
											}
										}),
								}}
								refetchRemoteData={refetchData}
							/>
						)}
					</div>
				</CardBody>
			</Card>

			<AddEditEvidenceModal
				dataActivity={dataActivity}
				isEdit={isEdit}
				isOpen={modal}
				handleToggleModal={handleToggleModal}
				activeTab={activeTab}
				toggleTab={toggleTab}
			/>
			<UploadEvidenceModal
				data={dataActivity}
				isOpen={modalUploadEvidence}
				handleToggleModal={handleToggleModalUploadEvidence}
				activeTab={activeTab}
				toggleTab={toggleTab}
				showDetails={true}
			/>
		</React.Fragment>
	);
};

export default ProductiveCycleActivitySection;
